/**
 * Columns for the NCR drill-downs — Qty Rejected, No. of NCRs and Returns
 * share one format (review item 7); Reject Value adds the money breakdown
 * (msg 9). Customer review 11 Sep (items 6, 7, 9).
 *
 * The money is the Reject Value cell's own per-NCR term (backend
 * `quality.reject_value`): Total Value = Qty Affected x Unit Price in the PO
 * currency, Converted Total Value = Total Value / Exchange Rate in the
 * customer's reporting currency, and that column sums to the cell.
 */
import { Badge } from '../../../components/ui/badge';
import NcrLink from '../../../components/common/NcrLink';
import { NCRPerformanceDrillRow } from '../../../lib/api';
import { DrilldownColumn, DrilldownFormatters, dateOf } from './columns';
import { ColumnOptions } from './columnsFor';

function severityClass(severity: string): string {
  if (severity === 'critical') return 'bg-red-100 text-red-800';
  if (severity === 'major') return 'bg-yellow-100 text-yellow-800';
  return 'bg-blue-100 text-blue-800';
}

type Row = NCRPerformanceDrillRow;

export function ncrColumns(f: DrilldownFormatters, options: ColumnOptions): DrilldownColumn<Row>[] {
  const rejectValue = options.filterType === 'Reject Value';
  const customerView = !options.isSupplier;

  // Reporting-currency view: customers only (suppliers see transaction currency).
  const convertedUnitPrice: DrilldownColumn<Row>[] = customerView ? [{
    key: 'converted_unit_price', label: 'Converted Unit Price', kind: 'money', align: 'right',
    title: "Unit Price / Exchange Rate, in the customer's reporting currency",
    value: (n) => n.converted_unit_price ?? null, currency: (n) => n.converted_currency,
  }] : [];

  const money: DrilldownColumn<Row>[] = rejectValue ? [
    {
      key: 'total_value', label: 'Total Value', kind: 'money', align: 'right',
      title: 'Qty Affected x Unit Price, in the PO currency',
      value: (n) => n.total_value ?? null, currency: (n) => n.currency,
    },
    ...(customerView ? [
      {
        key: 'converted_total_value', label: 'Converted Total Value', kind: 'money', align: 'right',
        title: 'Total Value in the reporting currency — the figure the Reject Value cell sums',
        value: (n: Row) => n.converted_total_value ?? null, currency: (n: Row) => n.converted_currency,
      } as DrilldownColumn<Row>,
    ] : []),
    { key: 'currency', label: 'Currency', value: (n) => n.currency || null },
    ...(customerView ? [
      {
        key: 'exchange_rate', label: 'Exchange Rate', align: 'right',
        title: '1 unit of the reporting currency in the PO currency, at the PO issue date',
        value: (n: Row) => n.exchange_rate ?? null,
      } as DrilldownColumn<Row>,
    ] : []),
  ] : [];

  return [
    {
      key: 'ncr_number', label: 'NCR Number', className: 'font-medium',
      value: (n) => n.ncr_number,
      render: (n) => <NcrLink ncrId={n.id} ncrNumber={n.ncr_number} />,
    },
    {
      key: 'customer_ncr_number', label: 'Customer NCR Number',
      title: "The customer's own (ERP) NCR number; blank for an NCR raised in the portal",
      value: (n) => n.customer_ncr_number || null,
    },
    { key: 'company_name', label: 'Customer', value: (n) => n.company_name },
    { key: 'supplier_group_name', label: 'Supplier Group', value: (n) => n.supplier_group_name || null },
    { key: 'supplier_name', label: 'Supplier Site', value: (n) => n.supplier_name },
    { key: 'part_number', label: 'Part Number', value: (n) => n.part_number },
    { key: 'part_description', label: 'Part Description', truncate: true, value: (n) => n.part_description },
    {
      key: 'severity', label: 'Severity', align: 'center',
      value: (n) => n.severity_display || n.severity,
      render: (n) => <Badge className={severityClass(n.severity)}>{n.severity}</Badge>,
    },
    { key: 'liability_display', label: 'Liability', value: (n) => n.liability_display ?? null },
    { key: 'quantity_affected', label: 'Qty Affected', kind: 'qty', align: 'right', value: (n) => n.quantity_affected },
    {
      key: 'reject_qty', label: 'Qty Rejected', kind: 'qty', align: 'right', className: 'text-red-600',
      title: 'The quantity the Qty Rejected cell sums',
      value: (n) => n.reject_qty,
    },
    {
      key: 'unit_price', label: 'Unit Price', kind: 'money', align: 'right',
      title: "The PO line's unit price, else the NCR's own — in the PO currency",
      value: (n) => n.effective_unit_price ?? n.unit_price, currency: (n) => n.currency,
    },
    ...convertedUnitPrice,
    ...money,
    { key: 'ncr_date', label: 'Date Raised', value: (n) => dateOf(f.formatDate, n.ncr_date) },
    { key: 'responded_by_name', label: 'Responded By', value: (n) => n.responded_by_name || null },
    {
      key: 'responded_date', label: 'Responded Date',
      title: "The supplier's containment / RCA response date",
      value: (n) => dateOf(f.formatDate, n.responded_date),
    },
    {
      key: 'time_taken_days', label: 'Time Taken (days)', kind: 'qty', align: 'right',
      title: 'Calendar days from Date Raised to Responded Date',
      value: (n) => n.time_taken_days ?? null,
    },
    {
      key: 'status', label: 'Status', align: 'center',
      value: (n) => n.status_display || n.status,
      render: (n) => <Badge variant="outline">{n.status_display || n.status}</Badge>,
    },
  ];
}
